import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Card, TextBox, Button, Spinner } from '../../../components/common';
import Navbar from '../../../components/common/Navbar';
import { userService } from '../../../api/services';
import { parsePhoneNumber } from 'react-phone-number-input';

/**
 * OAuth 가입 직후 누락된 프로필 정보(이름/연락처/이메일)를 보충 입력 받는 페이지.
 * - 카카오 비즈 인증 미통과 등으로 OAuth 응답에 phone/email 이 없을 때 회원이 직접 입력
 * - 콜백 페이지에서 누락 감지 시 이리로 redirect (loginRedirect 보존)
 */
export default function OAuthCompleteProfile() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const [missing, setMissing] = useState({ name: false, phone: false, email: false });

  useEffect(() => {
    userService.getProfile().then((profile) => {
      const missName = !profile.name || !profile.name.trim();
      const missPhone = !profile.phone || !profile.phone.trim();
      const missEmail = !profile.email || !profile.email.trim();
      setMissing({ name: missName, phone: missPhone, email: missEmail });
      // 이미 보충된 경우 바로 통과
      if (!missName && !missPhone && !missEmail) {
        const redirectTo = sessionStorage.getItem('loginRedirect') || '/';
        sessionStorage.removeItem('loginRedirect');
        navigate(redirectTo, { replace: true });
        return;
      }
      setName(profile.name || '');
      setPhone(profile.phone || '');
      setEmail(profile.email || '');
    }).catch(() => {
      navigate('/login', { replace: true });
    }).finally(() => setLoading(false));
  }, [navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (missing.name && !name.trim()) return alert(t('auth.alert_name_required', { defaultValue: '이름을 입력해주세요.' }));
    if (missing.phone && !phone.trim()) return alert(t('auth.alert_phone_required', { defaultValue: '연락처를 입력해주세요.' }));
    if (missing.email && !email.trim()) return alert(t('auth.alert_email_required', { defaultValue: '이메일을 입력해주세요.' }));

    setSubmitting(true);
    try {
      const payload = {};
      if (missing.name) payload.name = name.trim();
      if (missing.phone) {
        payload.phone = phone.trim();
        // phone E.164 (예: +821086934536) → ISO country code (예: 'KR') 자동 도출.
        // 유저가 phone 입력 시 선택한 국가코드를 country 컬럼에도 함께 저장 → 마이페이지 국가 자동 채움.
        try {
          const parsed = parsePhoneNumber(phone.trim());
          if (parsed?.country) payload.country = parsed.country;
        } catch { /* 파싱 실패 시 country 생략 */ }
      }
      if (missing.email) payload.email = email.trim();
      await userService.updateProfile(payload);
      const redirectTo = sessionStorage.getItem('loginRedirect') || '/';
      sessionStorage.removeItem('loginRedirect');
      navigate(redirectTo, { replace: true });
    } catch (err) {
      alert(t('auth.alert_profile_save_failed', { defaultValue: '프로필 저장에 실패했습니다.' }) + ' ' + (err?.message || ''));
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <>
        <Navbar />
        <main className="min-h-[calc(100vh-59px)] bg-mono-50 flex items-center justify-center">
          <Spinner />
        </main>
      </>
    );
  }

  return (
    <>
      <Navbar />
      <main className="min-h-[calc(100vh-59px)] bg-mono-50">
        <div className="w-full max-w-narrow mx-auto px-2.5 sm:px-5 pt-8 sm:pt-10 pb-12 sm:pb-16">
          <div className="flex justify-center">
            <img src="/images/logo/logo2.png" alt="VIP VAN TOUR" className="h-10" />
          </div>
          <Card variant="auth" className="mt-4">
            <h2 className="text-xl font-semibold text-mono-900 text-center">
              {t('auth.complete_profile_title', { defaultValue: '프로필 정보 입력' })}
            </h2>
            <p className="text-sm text-mono-500 text-center mb-2 leading-relaxed">
              {t('auth.complete_profile_desc', { defaultValue: '서비스 이용을 위해 누락된 정보를 입력해주세요.' })}
            </p>

            <form onSubmit={handleSubmit} className="space-y-4">
              {missing.name && (
                <TextBox
                  label={t('auth.name', { defaultValue: '이름' })}
                  placeholder={t('auth.name_placeholder', { defaultValue: '이름을 입력해주세요' })}
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  required
                />
              )}
              {missing.phone && (
                <TextBox
                  variant="phone"
                  label={t('auth.phone', { defaultValue: '연락처' })}
                  value={phone}
                  onChange={(v) => setPhone(v || '')}
                  required
                />
              )}
              {missing.email && (
                <TextBox
                  type="email"
                  label={t('auth.email', { defaultValue: '이메일' })}
                  placeholder="email@example.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                />
              )}
              <Button type="submit" level="level1" fullWidth disabled={submitting}>
                {submitting
                  ? t('auth.saving', { defaultValue: '저장 중...' })
                  : t('auth.complete_profile_submit', { defaultValue: '완료' })}
              </Button>
            </form>
          </Card>
        </div>
      </main>
    </>
  );
}
